import React, { useEffect, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { roleSwitchSchema, RoleSwitchSchema } from './schema'
import { useDistrictList } from '../../Registration/api'
import { useChangeUserRole } from '../api'
import FormBuilder from '../../../components/app/formBuilder'
import { X, UserX } from 'lucide-react'

interface RoleSwitchModalProps {
  isOpen: boolean
  onClose: () => void
  userData: any
}

const RoleSwitchModal: React.FC<RoleSwitchModalProps> = ({
  isOpen,
  onClose,
  userData,
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [formData, setFormData] = useState<RoleSwitchSchema | null>(null)

  const methods = useForm<RoleSwitchSchema>({
    resolver: zodResolver(roleSwitchSchema),
    mode: 'onChange',
    defaultValues: {
      name: userData?.name || '',
      email: userData?.email || '',
      mobile: userData?.mobile || userData?.phone || '',
      whatsapp_choice: 'Yes',
      whatsapp_no: userData?.mobile || userData?.phone || '',
      role: userData?.role || '',
      district: userData?.district || userData?.district_id || '',
    },
  })

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  useEffect(() => {
    console.log('userData structure:', userData)
  }, [userData])

  const { data: districtData } = useDistrictList()

  const whatsappChoice = useWatch({
    control: methods.control,
    name: 'whatsapp_choice',
  })

  const mobileNumber = useWatch({
    control: methods.control,
    name: 'mobile',
  })

  useEffect(() => {
    if (whatsappChoice === 'No' && mobileNumber) {
      methods.setValue('whatsapp_no', '')
    } else if (whatsappChoice === 'Yes') {
      methods.setValue('whatsapp_no', mobileNumber || '')
    }
  }, [whatsappChoice, mobileNumber, methods])

  // Reset form values when userData changes
  useEffect(() => {
    if (userData) {
      methods.reset({
        name: userData?.name || '',
        email: userData?.email || '',
        mobile: userData?.mobile || userData?.phone || '',
        whatsapp_choice: 'Yes',
        whatsapp_no: userData?.mobile || userData?.phone || '',
        role: userData?.role || '',
        district: userData?.district_id ? String(userData.district_id) : '',
      })
    }
  }, [userData, methods])

  // Initialize district and role when data is available
  useEffect(() => {
    if (userData && districtData?.data) {
      const matchingDistrict = districtData.data.find(
        (item: any) => String(item.id) === String(userData.district_id)
      )
      if (matchingDistrict) {
        methods.setValue('district_id', String(matchingDistrict.id))
        methods.setValue('district', matchingDistrict.district_name)
      }

      const roleOptions = [
        { name: 'Aspiring Entrepreneur', id: '2' },
        { name: 'Schools And Colleges', id: '3' },
        { name: 'Existing Entrepreneurs', id: '4' },
        { name: 'Research Institutions And Universities', id: '5' },
        { name: 'Industry Associations / Enablers', id: '6' },
        { name: 'Experts / Mentor', id: '7' },
        { name: 'Ed Club', id: '8' },
        { name: 'Students', id: '9' },
        { name: 'Guest', id: '10' },
      ]

      const matchingRole = roleOptions.find(
        (r) => Number(r.id) === Number(userData.role)
      )

      if (matchingRole) {
        methods.setValue('role', matchingRole.id)
        methods.setValue('role_name', matchingRole.name)
      }
    }
  }, [userData, districtData, methods])

  const districtOptions =
    districtData?.data?.map((item: any) => ({
      id: String(item.id),
      name: item.district_name,
    })) || []

  const onSuccess = (data: any) => {
    console.log('Role change successful:', data)
    onClose()
    setShowConfirmation(false)
  }

  const { mutate: changeRole, isPending } = useChangeUserRole(onSuccess)

  const onSubmit = (data: RoleSwitchSchema) => {
    setFormData(data)
    setShowConfirmation(true)
  }

  const handleConfirmRoleChange = () => {
    if (!formData) return
    const { whatsapp_choice, ...payload } = formData
    payload.whatsapp_no =
      whatsapp_choice === 'Yes' ? formData.mobile : formData.whatsapp_no
    changeRole(payload)
  }

  const handleCancelConfirmation = () => {
    setShowConfirmation(false)
    setFormData(null)
  }

  if (!isOpen) return null

  const personalInformation = [
    {
      name: 'name' as const,
      label: 'Name of Applicant',
      type: 'text' as const,
      required: true,
      disabled: true,
    },
    {
      name: 'email' as const,
      label: 'Email Address',
      type: 'email' as const,
      required: true,
      disabled: true,
    },
    {
      name: 'mobile' as const,
      label: 'Phone Number',
      type: 'number' as const,
      required: true,
      disabled: true,
    },
    {
      name: 'whatsapp_choice' as const,
      label: 'Is this your WhatsApp Number?',
      type: 'radio' as const,
      required: true,
      disabled: false,
      data: [
        { id: 1, radioLabel: 'Yes', value: 'Yes' },
        { id: 2, radioLabel: 'No', value: 'No' },
      ],
    },
    ...(whatsappChoice === 'No'
      ? [
          {
            name: 'whatsapp_no' as const,
            label: 'WhatsApp Number',
            type: 'number' as const,
            required: true,
            disabled: false,
          },
        ]
      : []),
    {
      name: 'role_name' as const,
      label: 'New Role',
      type: 'custom_select' as const,
      placeholder: 'Select New Role',
      required: true,
      disabled: false,
      desc: 'name',
      descId: 'id',
      id: 'role',
      data: [
        { name: 'Aspiring Entrepreneur', id: '2' },
        { name: 'Schools And Colleges', id: '3' },
        { name: 'Existing Entrepreneurs', id: '4' },
        { name: 'Research Institutions And Universities', id: '5' },
        { name: 'Industry Associations / Enablers', id: '6' },
        { name: 'Experts / Mentor', id: '7' },
        { name: 'Ed Club', id: '8' },
        { name: 'Students', id: '9' },
        { name: 'Guest', id: '10' },
      ],
    },
    {
      name: 'district' as const,
      label: 'District',
      type: 'custom_select' as const,
      placeholder: 'Select District',
      required: true,
      disabled: false,
      desc: 'name',
      descId: 'id',
      id: 'district_id',
      data: districtOptions,
    },
  ]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {!showConfirmation ? (
          <>
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <UserX className="h-6 w-6 text-orange-500" />
                <h2 className="text-xl font-semibold">Switch Role</h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={methods.handleSubmit(onSubmit)} className="p-6">
              <FormProvider {...methods}>
                <div className="space-y-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> Changing your role will log you out
                      of the system. You`ll need to log back in with your new
                      role permissions.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <FormBuilder data={personalInformation} edit />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isPending || !methods.formState.isValid}
                      className={`flex-1 px-4 py-2 text-white font-semibold rounded-md transition ${
                        isPending || !methods.formState.isValid
                          ? 'bg-orange-300 cursor-not-allowed'
                          : 'bg-orange-500 hover:bg-orange-600'
                      }`}
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </FormProvider>
            </form>
          </>
        ) : (
          <div className="p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <UserX className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Confirm Role Change
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to change your role? This action will log
              you out immediately, and you`ll need to sign in again with your
              new role permissions.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                type="button"
                onClick={handleCancelConfirmation}
                disabled={isPending}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRoleChange}
                disabled={isPending}
                className={`px-4 py-2 text-white font-semibold rounded-md transition ${
                  isPending
                    ? 'bg-red-400 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {isPending ? 'Changing Role...' : 'Yes, Change Role'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default RoleSwitchModal
